/**
 * LLM tool definitions for template retrieval and application.
 *
 * `retrieve_template` — returns ranked template candidates for a query.
 * `apply_template`    — opens a FactSet template into the workbook.
 *
 * Both require a `TemplateToolContext` injected at call time. The
 * template catalog, ranker, and host opener are runtime/session-scoped
 * dependencies, so they cannot be imported statically.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import type {
  TemplateCatalogRow,
  TemplateCandidateRetrievalResult,
} from "./excelAIService";
import type {
  OpenTemplatePhase,
  OpenTemplateResult,
} from "./services/openTemplate";

// ---------------------------------------------------------------------------
// Identifier validation
// ---------------------------------------------------------------------------

/**
 * SYMBOL[-CLASS]-COUNTRY where SYMBOL is one or more alphanumerics
 * with optional dot (e.g. "BRK.B"), CLASS is optional (e.g. "A" in
 * "GOOG-A-US" — rare but valid), COUNTRY is exactly two uppercase
 * letters. Strict: rejects leading/trailing whitespace, lowercase,
 * missing country. Catches "Apple", "apple inc", "AAPL" (no
 * country), " AAPL-US " (whitespace), "aapl-us" (lowercase);
 * accepts AAPL-US, BRK.B-US, 7203-JP, VOD-GB, GOOG-A-US.
 *
 * Deliberately does NOT call `.trim()` — the planner's prompt
 * contract is that it emits clean SYMBOL-COUNTRY strings. Any
 * whitespace in the field is a planner bug we want to surface
 * immediately (the user gets re-asked for the ticker, which is
 * the same UX as a missing ticker).
 */
export function isValidFactSetIdentifier(raw: string): boolean {
  return /^[A-Z0-9]+(\.[A-Z0-9]+)?(-[A-Z0-9]+)?-[A-Z]{2}$/.test(raw);
}

/**
 * Runtime-observed result of an `apply_template` tool call.
 *
 * This is intentionally separate from the JSON string returned to the LLM.
 * The JSON response lets the model continue reasoning; this side-channel
 * outcome lets the orchestrator update step status and workbook targeting from
 * host truth without parsing model-visible text.
 */
export interface ApplyTemplateOutcome {
  /**
   * Whether the tool reached a terminal successful host open.
   *
   * `false` covers validation errors, bridge unavailability, unknown template
   * keys, missing required identifiers, and non-abort host-open failures.
   */
  success: boolean;
  /**
   * Verbatim template key requested by the tool call.
   *
   * Optional because missing-key validation failures have no usable key.
   */
  templateKey?: string;
  /** Human-readable template name from the entitled catalog when known. */
  templateName?: string;
  /**
   * Workbook target used or attempted by the tool.
   *
   * For failures before host open, this is the parsed target value or the
   * default `"current"`. For success, it is the host-reported final target.
   */
  targetWorkbook: "current" | "new";
  /**
   * Present when a requested/current-workbook open was redirected to a new
   * workbook, usually to avoid Excel's duplicate `Identifier` named-item modal.
   */
  retargetedFrom?: "current";
  /** Sheet names imported by the successful template open. */
  sheetNames?: string[];
  /** FactSet refresh result reported by `openTemplateInExcel()` on success. */
  refreshStatus?: OpenTemplateResult["refresh"];
  /** End-to-end template open duration reported by the host opener. */
  durationMs?: number;
  /** User/debug-facing error message for unsuccessful outcomes. */
  error?: string;
}

// ---------------------------------------------------------------------------
// Context injected by the tool-loop caller
// ---------------------------------------------------------------------------

/**
 * Runtime context injected by the tool-loop caller.
 *
 * `retrieve_template` needs the entitled template catalog and a ranking
 * function. `apply_template` additionally needs host operations for
 * bridge availability checks, workbook conflict checks, and the actual
 * template-open side effect. Keeping these dependencies injected makes
 * the tool executors testable and avoids static imports of session-only
 * host state.
 */
export interface TemplateToolContext {
  /**
   * Entitled template catalog for the current session.
   *
   * Loaded from the FactSet host / entitlement layer before the LLM tool
   * loop runs. Empty catalogs mean template tools are unavailable for the
   * session.
   */
  catalog: TemplateCatalogRow[];

  /**
   * Ranks entitled template candidates for a natural-language query.
   *
   * Uses semantic ranking when embeddings are available and falls back to
   * lexical/category matching otherwise.
   *
   * @param query      Banker-style query describing the desired template.
   * @param templates  Entitled template catalog to search.
   * @param topK       Maximum number of candidates to return.
   * @param signal     Optional cancellation signal.
   */
  rankTemplateCandidates: (
    query: string,
    templates: TemplateCatalogRow[],
    topK: number,
    signal?: AbortSignal,
  ) => Promise<TemplateCandidateRetrievalResult>;

  /**
   * Opens a FactSet template into the active or a new Excel workbook.
   *
   * This is the dependency-injected wrapper around `openTemplateInExcel`.
   * The host implementation runs the full template chain: host-ready gate,
   * download/instantiate, manual-calc bracket, worksheet insertion, cover
   * activation, and optional FactSet data refresh.
   *
   * @param key      Verbatim template key from the entitled catalog.
   * @param options  Optional host-open configuration:
   *                 - `inputs`: template input values such as
   *                   `{ main_identifier: "AAPL-US" }`.
   *                 - `targetWorkbook`: `"current"` inserts into the
   *                   active workbook; `"new"` creates a fresh workbook.
   *                 - `identifierConflictAction`: duplicate `Identifier`
   *                   handling strategy.
   *                 - `signal`: cancellation signal observed by waits.
   *                 - `onPhase`: progress callback for host phase updates.
   * @returns        Host result with imported sheet names, refresh status,
   *                 target workbook, duration, and retarget metadata.
   * @throws         Host errors, including `OpenTemplateError`, or
   *                 `DOMException("AbortError")` on cancellation.
   */
  openTemplate: (
    key: string,
    options?: {
      inputs?: Record<string, string>;
      targetWorkbook?: "current" | "new";
      identifierConflictAction?: "retargetToNewWorkbook" | "replaceExisting";
      signal?: AbortSignal;
      onPhase?: (phase: OpenTemplatePhase, data?: unknown) => void;
    },
  ) => Promise<OpenTemplateResult>;

  /**
   * Returns whether the current session can open templates through the
   * FactSet intent bridge. When false, `apply_template` returns a
   * structured tool error instead of attempting a host call.
   */
  canOpenTemplates: () => boolean;

  /**
   * Checks whether the current workbook already has a workbook-level
   * `Identifier` named item. FactSet templates commonly create this name;
   * opening another template into such a workbook can trigger Excel's
   * duplicate-name modal, so the tool auto-retargets to a new workbook
   * when this returns true.
   *
   * @returns True when the current workbook has the `Identifier` name.
   * @throws  May throw if workbook metadata cannot be read. Callers catch
   *          and proceed conservatively.
   */
  workbookHasIdentifierName: () => Promise<boolean>;

  /**
   * Side channel for host-observed template outcomes. The LLM still receives
   * the JSON tool response; this callback lets the orchestrator update step
   * status and workbook targeting from runtime ground truth.
   *
   * Callers should scope collection to a single `executeStep()` invocation so
   * outcomes cannot leak across plan steps.
   */
  onApplyTemplateOutcome?: (outcome: ApplyTemplateOutcome) => void;
}

// ---------------------------------------------------------------------------
// retrieve_template
// ---------------------------------------------------------------------------

/** Number of candidates returned by `retrieve_template`. */
const RETRIEVE_TOP_K = 20;

export const RETRIEVE_TEMPLATE_TOOL: ChartCapabilityToolDef = {
  name: "retrieve_template",
  description:
    `Searches the user's entitled FactSet template library and returns the top matching templates.\n\n` +
    `Use this tool ONLY when the user explicitly asks for templates/catalog lookup, or asks to open / use / load / launch a FactSet template, model template, workbook template, or available template from the catalog.\n\n` +
    `Do NOT call this tool merely because the user names a canonical model family such as DCF, LBO, WACC, comps / comparables, public company profile, 3-statement, M&A / accretion-dilution, returns waterfall, or credit analysis.\n` +
    `Do NOT call this tool for requests to build, create, make, generate, draft, construct, configure, or set up a financial artifact/model unless the user explicitly asks for a template. Headless, reusable, configurable, dynamic, input-driven, company-agnostic, or parameterized model requests are scratch-build requests unless the user also explicitly asks for a template.\n` +
    `Do NOT call this tool for requests to BUILD a specific artifact (table, chart, list, sheet, column, row, named range, formula, summary, breakdown), even when the request mentions a company. Those go through the normal plan steps (fill / formula / format / table / chart / create_sheet / add_named_object). "Build a revenue table for Apple" is edit, not retrieve_template.\n\n` +
    `When you do call it: write a banker-style query — include workflow synonyms ("valuation templates" → "valuation DCF public company comps transaction comps accretion dilution multiples"; "company overview templates" → "profile tear sheet summary financials estimates"). The synonym examples always reference the QUALIFIED noun ("valuation templates", "company overview templates") — never the bare workflow noun, so the planner does not generalise "build a company overview for Apple" into a template retrieval. Returns candidates with key, name, category, description, extendedDescription, language, inputTypes. Treat as a pool — re-rank for the user's actual ask.`,
  inputSchema: {
    type: "object",
    properties: {
      query: {
        type: "string",
        description:
          "Natural-language description of the desired template (e.g. 'public company comps for tech sector')",
      },
    },
    required: ["query"],
    additionalProperties: false,
  },
};

/**
 * Execute the `retrieve_template` tool call. Searches the entitled
 * template catalog using semantic retrieval when available and
 * returns the top-K candidates as a JSON string for the LLM.
 */
export async function executeRetrieveTemplateTool(
  rawInput: unknown,
  context: TemplateToolContext,
  signal?: AbortSignal,
): Promise<string> {
  const input = rawInput as Record<string, unknown> | null;
  const query = typeof input?.query === "string" ? input.query.trim() : "";
  if (!query) {
    return JSON.stringify({ error: "Missing required parameter: query" });
  }
  if (context.catalog.length === 0) {
    return JSON.stringify({ candidates: [], note: "No templates available in the current session." });
  }

  const result = await context.rankTemplateCandidates(
    query,
    context.catalog,
    RETRIEVE_TOP_K,
    signal,
  );

  const candidates = result.candidates.map((c) => ({
    key: c.template.key,
    name: c.template.name,
    category: c.template.category ?? null,
    description: c.template.description ?? null,
    extendedDescription: c.template.extendedDescription ?? null,
    language: c.template.language ?? null,
    inputTypes: c.template.inputTypes ?? [],
  }));

  return JSON.stringify({ candidates, confidence: result.confidence });
}

// ---------------------------------------------------------------------------
// apply_template
// ---------------------------------------------------------------------------

/**
 * LLM tool definition for `apply_template`.
 *
 * The executor calls this tool when it needs to instantiate a selected
 * FactSet template into Excel. After success, the workbook contains the
 * imported template sheets and subsequent plan steps can edit/fill/format
 * that workbook normally.
 */
export const APPLY_TEMPLATE_TOOL: ChartCapabilityToolDef = {
  name: "apply_template",
  description:
    `Opens a FactSet template into the workbook.\n\n` +
    `Use this when applying a specific template key to pull a template into the workbook. ` +
    `The templateKey must come from retrieve_template results, prior context, or input; do not invent keys.\n\n` +
    `Inputs:\n` +
    `- templateKey: exact key for the entitled template to open.\n` +
    `- companyIdentifier: optional FactSet SYMBOL-COUNTRY identifier such as "AAPL-US"; required only when the template accepts main_identifier.\n` +
    `- targetWorkbook: optional "current" or "new"; defaults to "current".\n\n` +
    `Returns imported sheet names, target workbook, refresh status, retarget metadata, and duration. After success, continue with any remaining plan steps.`,
  inputSchema: {
    type: "object",
    properties: {
      templateKey: {
        type: "string",
        description:
          "Exact template key from retrieve_template results, prior context, or input.",
      },
      companyIdentifier: {
        type: "string",
        description:
          "Optional FactSet SYMBOL-COUNTRY identifier, for example 'AAPL-US' or '7203-JP'. Required only when the template accepts main_identifier.",
      },
      targetWorkbook: {
        type: "string",
        enum: ["current", "new"],
        description:
          "Where to open the template. Defaults to 'current'. Use 'new' when the user asked for a separate workbook.",
      },
    },
    required: ["templateKey"],
    additionalProperties: false,
  },
};

/**
 * Execute the `apply_template` tool call.
 *
 * Validation order:
 * 1. `templateKey` is present.
 * 2. `targetWorkbook`, if provided, is `"current"` or `"new"`.
 * 3. The FactSet intent bridge can open templates.
 * 4. `templateKey` exists in the entitled catalog.
 * 5. `companyIdentifier`, if consumed by the matched template, is a valid SYMBOL-COUNTRY value.
 * 6. Templates requiring `main_identifier` receive `companyIdentifier`.
 *
 * The executor strips unused identifiers for templates that do not accept
 * `main_identifier`, auto-retargets current-workbook opens to a new workbook
 * when the active workbook already has an `Identifier` name, delegates the
 * actual host work to `context.openTemplate`, emits an optional
 * `ApplyTemplateOutcome` side-channel event, and returns a JSON string for the
 * LLM tool response.
 *
 * @param rawInput  Parsed LLM tool arguments.
 * @param context   Runtime template context containing catalog, ranker, and
 *                  host-open dependencies.
 * @param signal    Optional cancellation signal passed to the host opener.
 * @returns         JSON string. Success includes `success`, `templateName`,
 *                  `sheetNames`, `targetWorkbook`, `retargetedFrom`,
 *                  `refreshStatus`, `instantiatedWithInputs`, and
 *                  `durationMs`; failures include an `error` field.
 * @throws          Re-throws `DOMException("AbortError")` so the orchestrator
 *                  can handle cancellation uniformly.
 */
export async function executeApplyTemplateTool(
  rawInput: unknown,
  context: TemplateToolContext,
  signal?: AbortSignal,
): Promise<string> {
  const input = rawInput as Record<string, unknown> | null;
  const templateKey = typeof input?.templateKey === "string"
    ? input.templateKey.trim()
    : "";
  const companyIdentifier = typeof input?.companyIdentifier === "string"
    ? input.companyIdentifier
    : undefined;
  const rawTargetWorkbook = input?.targetWorkbook;
  const targetWorkbookInput: "current" | "new" | undefined =
    rawTargetWorkbook === "current" || rawTargetWorkbook === "new"
      ? rawTargetWorkbook
      : undefined;
  const emit = (outcome: ApplyTemplateOutcome): void => {
    context.onApplyTemplateOutcome?.(outcome);
  };
  const fail = (error: string, details?: Partial<ApplyTemplateOutcome>): string => {
    emit({
      success: false,
      templateKey: templateKey || undefined,
      targetWorkbook: targetWorkbookInput ?? "current",
      error,
      ...details,
    });
    return JSON.stringify({ error });
  };

  if (!templateKey) {
    return fail("Missing required parameter: templateKey");
  }

  if (
    rawTargetWorkbook != null &&
    rawTargetWorkbook !== "current" &&
    rawTargetWorkbook !== "new"
  ) {
    return fail(`Invalid targetWorkbook value "${String(rawTargetWorkbook)}". Must be "current" or "new".`);
  }

  if (!context.canOpenTemplates()) {
    return fail("Template opens require the FactSet intent bridge. Open the workbook inside FactSet Workstation.");
  }

  const matched = context.catalog.find((t) => t.key === templateKey);
  if (!matched) {
    return fail(`Template key "${templateKey}" not found in the entitled catalog. Use retrieve_template to find valid keys.`);
  }

  const acceptsIdentifier = matched.inputTypes?.includes("main_identifier") ?? false;

  if (acceptsIdentifier && companyIdentifier && !isValidFactSetIdentifier(companyIdentifier)) {
    return fail(`Invalid company identifier "${companyIdentifier}". Must be SYMBOL-COUNTRY format (e.g. AAPL-US, MSFT-US, 7203-JP).`, {
      templateName: matched.name ?? templateKey,
    });
  }

  if (acceptsIdentifier && !companyIdentifier) {
    return fail(`Template "${matched.name ?? templateKey}" requires a company identifier (main_identifier). Provide companyIdentifier in SYMBOL-COUNTRY format.`, {
      templateName: matched.name ?? templateKey,
    });
  }

  const effectiveIdentifier = acceptsIdentifier ? companyIdentifier : undefined;

  let targetWorkbook: "current" | "new" = targetWorkbookInput ?? "current";
  let retargeted = false;

  if (targetWorkbook === "current") {
    try {
      const hasIdentifier = await context.workbookHasIdentifierName();
      if (hasIdentifier) {
        targetWorkbook = "new";
        retargeted = true;
      }
    } catch {
      // Best-effort: if metadata read fails, let the host opener handle conflicts.
    }
  }

  const inputs: Record<string, string> | undefined = effectiveIdentifier
    ? { main_identifier: effectiveIdentifier }
    : undefined;

  try {
    const result = await context.openTemplate(templateKey, {
      inputs,
      targetWorkbook,
      signal,
    });
    const retargetedFrom = result.retargetedFrom ?? (retargeted ? "current" : undefined);

    emit({
      success: true,
      templateKey,
      templateName: matched.name ?? templateKey,
      sheetNames: result.sheetNames,
      targetWorkbook: result.targetWorkbook,
      retargetedFrom,
      refreshStatus: result.refresh,
      durationMs: result.durationMs,
    });

    return JSON.stringify({
      success: true,
      templateName: matched.name ?? templateKey,
      sheetNames: result.sheetNames,
      targetWorkbook: result.targetWorkbook,
      retargetedFrom,
      refreshStatus: result.refresh,
      instantiatedWithInputs: result.instantiatedWithInputs,
      durationMs: result.durationMs,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw err;
    }
    const message = err instanceof Error ? err.message : String(err);
    const error = `Template open failed: ${message}`;
    emit({
      success: false,
      templateKey,
      templateName: matched.name ?? templateKey,
      targetWorkbook,
      error,
    });
    return JSON.stringify({
      error,
      templateName: matched.name ?? templateKey,
      targetWorkbook,
    });
  }
}

/** Template tools available to planner/chat/executor scopes. */
export const TEMPLATE_TOOLS: readonly ChartCapabilityToolDef[] = [
  RETRIEVE_TEMPLATE_TOOL,
  APPLY_TEMPLATE_TOOL,
];
